import React, {  useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  BarChart3,
  Clock,
  Brain,
  Target,
  Eye,
  Play,
  Store,
  Search,
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const RetailEcommercePage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  

  const challenges = [
    'Understanding customer sentiment across thousands of reviews and feedback',
    'Managing inventory and customer experience across multiple sales channels',
    'Lack of personalized customer insights and recommendation systems',
    'Difficulty in analyzing large volumes of customer data and market trends',
    'Need for responsive and engaging e-commerce platforms',
    'Coordinating complex digital marketing and customer service operations'
  ];

  const solutions = [
    // {
    //   name: 'Summarizer IQ',
    //   path: '/products/ai#summarizer-iq',
    //   description: 'AI-powered content analysis and summarization for market research and business intelligence.',
    //   benefits: ['Market Analysis', 'Content Summarization', 'Business Intelligence']
    // },
    {
      name: 'Websites',
      path: '/products/it/Websites',
      description: 'Custom e-commerce platforms and retail websites designed for optimal customer experience.',
      benefits: ['E-commerce Platforms', 'Customer Portals', 'Mobile Commerce']
    },
    {
      name: 'WhispeRAGon',
      path: '/products/ai/whisperagon',
      description: 'Voice-native RAG that turns conversations into accurate, grounded answers.',
      benefits: ['Cited answers', 'Context retrieval', 'Voice transcription']
    },
        {
      name: 'Style Sync',
      path: '/products/style-sync',
      description: 'Centralize palettes, typography, and components with automated checks.',
      benefits: ['Auto validation', 'Asset libraries', 'Brand consistency']
    },
  ];

  const keyFeatures = [
    {
      icon: Brain,
      title: 'Customer Intelligence',
      description: 'Advanced analytics and AI-powered insights that understand customer behavior and preferences.'
    },
    {
      icon: Target,
      title: 'Personalized Experiences',
      description: 'Tailored customer journeys and personalized recommendations that drive engagement and sales.'
    },
    {
      icon: BarChart3,
      title: 'Omnichannel Optimization',
      description: 'Seamless integration across all retail channels for consistent customer experiences.'
    },
    {
      icon: TrendingUp,
      title: 'Data-Driven Growth',
      description: 'Actionable insights from customer data that drive strategic business decisions and growth.'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Sales Performance',
      description: 'AI-powered insights and personalized experiences significantly increase conversion rates and sales.',
      metric: '65% sales increase'
    },
    {
      icon: Users,
      title: 'Customer Satisfaction',
      description: 'Enhanced customer experiences and personalized service improve satisfaction and loyalty.',
      metric: '80% satisfaction boost'
    },
    {
      icon: Clock,
      title: 'Operational Efficiency',
      description: 'Automated processes and intelligent analytics reduce operational overhead and improve efficiency.',
      metric: '55% efficiency gain'
    },
    {
      icon: BarChart3,
      title: 'Market Intelligence',
      description: 'Advanced analytics provide deep insights into market trends and customer behavior patterns.',
      metric: '90% better insights'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-pink-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 4,
                delay: i * 0.3,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-pink-500 to-rose-600 neon-glow">
                  <ShoppingCart className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-pink-400 uppercase tracking-wide">Smart Commerce Technology</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Retail & <span className="bg-linear-to-r from-pink-500 to-rose-600 bg-clip-text text-transparent">E-commerce</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-pink-300"
              >
                Revolutionizing Retail Through Customer Intelligence
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                The retail and e-commerce landscape is evolving rapidly, driven by changing consumer behaviors, digital-first 
                shopping experiences, and the need for personalized customer engagement. Success requires deep understanding 
                of customer preferences, seamless omnichannel experiences, and data-driven decision making. At amasQIS.ai, 
                we empower retailers with AI-powered customer intelligence, advanced analytics, and cutting-edge e-commerce 
                platforms to thrive in the digital marketplace.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-pink-500 to-rose-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-pink-500/25 transition-all duration-300"
                >
                  Transform Retail
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-pink-500 to-rose-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/retail-industry.png'
                    alt="Retail & E-commerce Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-pink-500/20 rounded-lg backdrop-blur-sm">
                          <ShoppingCart className="w-5 h-5 text-pink-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Commerce Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-pink-500">
                    <Store className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">RETAIL</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Challenges in Modern Retail & E-commerce
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Today's retail environment demands innovative approaches to customer engagement, data analysis, and omnichannel experiences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <Search className="w-5 h-5 text-pink-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solutions for Retail & E-commerce
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Comprehensive retail solutions designed to enhance customer experiences, optimize operations, and drive growth in the digital marketplace.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-pink-400 group-hover:text-pink-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-pink-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-rose-400" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Retailers Choose amasQIS.ai
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              We combine deep retail expertise with cutting-edge AI to deliver solutions that truly understand and serve your customers.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-8 h-8 text-pink-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Retail Impact
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Transform your retail operations with quantifiable improvements in sales, customer satisfaction, and operational efficiency.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-pink-500/20">
                      <Icon className="w-6 h-6 text-pink-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-rose-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Transform Your Retail Experience?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-8">
              Join leading retailers that trust amasQIS.ai to deliver intelligent, customer-centric solutions that drive growth and success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-pink-500 to-rose-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-pink-500/25 transition-all duration-300"
              >
                Transform Retail
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { RetailEcommercePage };
